import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  EnvelopeSimple, 
  CircleNotch,
  Info,
  Sparkle
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import API_URL from '../config';

export default function EmailsPage() {
  const [summary, setSummary] = useState('');
  const [emailCount, setEmailCount] = useState(0);
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'ready' | 'error'
  const abortRef = useRef(null);

  // Cancel in-flight request when leaving the page so it doesn't block the rest of the app
  useEffect(() => () => {
    if (abortRef.current) abortRef.current.abort();
  }, []);

  const fetchSummary = async () => {
    setStatus('loading');
    abortRef.current = new AbortController();
    try {
      const response = await axios.get(`${API_URL}/api/client/email-summary`, {
        signal: abortRef.current.signal,
      });
      setSummary(response.data.summary || '');
      setEmailCount(response.data.email_count || 0);
      setStatus('ready');
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      setStatus('error');
      toast.error('Failed to load email summary');
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto" data-testid="emails-page">
        {/* Header */}
        <div className="mb-8">
          <p className="label-uppercase mb-1" style={{ color: '#1d57a5' }}>Communications</p>
          <h1 className="text-3xl font-bold tracking-tight" style={{ color: '#141b4d' }}>
            Email Summary
          </h1>
        </div>

        {/* Idle state — explain the feature, let user opt in */}
        {status === 'idle' && (
          <div className="card-swiss" data-testid="emails-idle-card">
            <div className="flex items-start gap-4 mb-5 pb-5 border-b" style={{ borderColor: '#d4cfc5' }}>
              <div className="w-11 h-11 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#ffd100' }}>
                <Sparkle weight="bold" className="w-5 h-5" style={{ color: '#141b4d' }} />
              </div>
              <div>
                <h2 className="text-lg font-semibold" style={{ color: '#141b4d', fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  Get a quick summary of your recent emails with our team
                </h2>
                <p className="text-sm mt-2 leading-relaxed" style={{ color: '#52525B' }}>
                  We can review the most recent email threads between you and our legal team and pull together a plain-English recap of what's been discussed, what's been decided, and what (if anything) needs your attention.
                </p>
                <p className="text-xs mt-3" style={{ color: '#9a9488' }}>
                  This usually takes 10–30 seconds. Please stay on this page while it loads — navigating away may freeze your browser.
                </p>
              </div>
            </div>
            <button
              onClick={fetchSummary}
              className="btn-primary inline-flex items-center justify-center gap-2 w-full sm:w-auto"
              data-testid="analyze-emails-btn"
            >
              <Sparkle weight="bold" className="w-5 h-5" />
              Analyze recent email activity
            </button>
          </div>
        )}

        {/* Soft contact callout — visible in every state except loading */}
        {status !== 'loading' && (
          <div className="card-swiss mt-6" style={{ backgroundColor: '#f2e9db', borderTop: '3px solid #ffd100', border: 'none' }}>
            <div className="flex items-start gap-3">
              <Info weight="duotone" className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#1d57a5' }} />
              <p className="text-sm leading-relaxed" style={{ color: '#101820' }}>
                For the fastest reply, just respond to your most recent email from us. If you'd prefer to start a fresh message, you can email us at{' '}
                <a
                  href="mailto:clients@indielaw.com"
                  className="font-medium hover:underline"
                  style={{ color: '#1d57a5' }}
                  data-testid="emails-page-mailto"
                >
                  clients@indielaw.com
                </a>{' '}
                at any time.
              </p>
            </div>
          </div>
        )}

        {/* Loading state — scoped to the card */}
        {status === 'loading' && (
          <div className="card-swiss" data-testid="emails-loading-card">
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <CircleNotch weight="bold" className="w-8 h-8 animate-spin mb-3" style={{ color: '#ffd100' }} />
              <p className="font-medium" style={{ color: '#141b4d' }}>Reviewing your email threads…</p>
              <p className="text-sm mt-1 max-w-md" style={{ color: '#9a9488' }}>
                This usually takes 10–30 seconds. Please stay on this page while we work — clicking away may freeze your browser.
              </p>
            </div>
          </div>
        )}

        {/* Error state */}
        {status === 'error' && (
          <div className="card-swiss" data-testid="emails-error-card">
            <p className="font-medium mb-2" style={{ color: '#b91c1c' }}>We couldn't load your email summary.</p>
            <p className="text-sm mb-4" style={{ color: '#52525B' }}>Please try again, or email us directly.</p>
            <button
              onClick={fetchSummary}
              className="btn-primary inline-flex items-center justify-center gap-2 w-full sm:w-auto"
              data-testid="retry-emails-btn"
            >
              Try again
            </button>
          </div>
        )}

        {/* Ready state — summary */}
        {status === 'ready' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="card-swiss">
              <div className="flex flex-wrap items-center gap-3 mb-4 pb-4 border-b" style={{ borderColor: '#d4cfc5' }}>
                <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#ffd100' }}>
                  <EnvelopeSimple weight="bold" className="w-5 h-5" style={{ color: '#141b4d' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium" style={{ color: '#141b4d' }}>Recent Communications</p>
                  <p className="text-sm" style={{ color: '#9a9488' }}>
                    Based on {emailCount} recent email{emailCount !== 1 ? 's' : ''} with our team
                  </p>
                </div>
                <button
                  onClick={fetchSummary}
                  className="text-sm font-medium hover:underline"
                  style={{ color: '#1d57a5' }}
                  data-testid="refresh-emails-btn"
                >
                  Refresh
                </button>
              </div>

              <div className="prose prose-sm max-w-none" style={{ color: '#101820' }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(summary) }}
              />
            </div>

            <div className="flex items-start gap-3 p-4" style={{ backgroundColor: '#f2e9db', borderTop: '3px solid #ffd100' }}>
              <Info weight="duotone" className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#1d57a5' }} />
              <p className="text-sm" style={{ color: '#9a9488' }}>
                This summary is generated by AI and may not be perfectly accurate. For the most up-to-date information, please email us directly or refer to your original email threads.
              </p>
            </div>
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
}
